import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { AccountConfiguration, ParsingField } from '@/types/onboarding';
import { useToast } from '@/hooks/use-toast';

interface MappedData {
  account_number?: string;
  property_name?: string;
  unit_number?: string;
  commodity_type?: string;
  utility_type?: string;
  vendor_name?: string;
  supplier_name?: string;
  [key: string]: unknown;
}

// Fetch imported accounts from staged data
export function useImportedAccounts(projectId: string | undefined) {
  return useQuery({
    queryKey: ['imported-accounts', projectId],
    queryFn: async () => {
      if (!projectId) return [];

      // Get accounts from staged data (imported in Stage 2)
      const { data: batches, error: batchError } = await supabase
        .from('tum_onboarding_import_batches')
        .select('id')
        .eq('project_id', projectId)
        .eq('batch_type', 'accounts')
        .eq('status', 'completed');

      if (batchError) throw batchError;
      if (!batches || batches.length === 0) return [];

      const batchIds = batches.map(b => b.id);

      // CRITICAL FIX: Load ALL rows (up to 10,000) using chunked loading to avoid Supabase limits
      // First get total count
      const { count: totalCount, error: countError } = await supabase
        .from('tum_onboarding_staged_data')
        .select('id', { count: 'exact', head: true })
        .in('batch_id', batchIds)
        .in('import_status', ['imported', 'ready', 'ready_for_validation', 'validated']);

      if (countError) throw countError;

      console.log(`📊 Loading ${totalCount} imported accounts for Mapping stage`);

      // Fetch all rows in chunks (Supabase PostgREST default max is 1000)
      const CHUNK_SIZE = 1000;
      const MAX_ROWS = 10000; // Maximum rows to load
      const allRows: any[] = [];
      const rowsToLoad = Math.min(totalCount || 0, MAX_ROWS);

      for (let offset = 0; offset < rowsToLoad; offset += CHUNK_SIZE) {
        const { data: chunkData, error: chunkError } = await supabase
          .from('tum_onboarding_staged_data')
          .select('*')
          .in('batch_id', batchIds)
          .in('import_status', ['imported', 'ready', 'ready_for_validation', 'validated'])
          .order('row_number', { ascending: true })
          .range(offset, offset + CHUNK_SIZE - 1);

        if (chunkError) throw chunkError;

        if (chunkData && chunkData.length > 0) {
          allRows.push(...chunkData);
          console.log(`📥 Loaded ${chunkData.length} accounts (total: ${allRows.length}/${rowsToLoad})`);
        }
      }

      console.log(`✅ Loaded ${allRows.length} total imported accounts`);

      return (allRows || []).map(row => {
        const mapped = row.mapped_data as MappedData | null;
        const source = row.source_data as MappedData | null;
        
        return {
          id: row.id,
          account_number: mapped?.account_number || source?.account_number,
          property_name: mapped?.property_name || source?.property_name,
          unit_number: mapped?.unit_number || source?.unit_number,
          commodity_type: mapped?.commodity_type || mapped?.utility_type || source?.commodity_type,
          vendor_name: mapped?.vendor_name || mapped?.supplier_name || source?.vendor_name,
          raw_data: row.source_data,
        };
      });
    },
    enabled: !!projectId,
  });
}

// Fetch account configurations for a project
export function useAccountConfigurations(projectId: string | undefined) {
  return useQuery({
    queryKey: ['account-configurations', projectId],
    queryFn: async () => {
      if (!projectId) return [];

      const { data, error } = await (supabase as any)
        .from('onboarding_account_configurations')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as AccountConfiguration[];
    },
    enabled: !!projectId,
  });
}

// Save or update account configuration
export function useSaveAccountConfiguration() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (config: Partial<AccountConfiguration> & { project_id: string; imported_account_id?: string }) => {
      // Check if configuration exists
      const { data: existing } = await (supabase as any)
        .from('onboarding_account_configurations')
        .select('id')
        .eq('project_id', config.project_id)
        .eq('imported_account_id', config.imported_account_id)
        .maybeSingle();

      if (existing) {
        // Update existing
        const { data, error } = await (supabase as any)
          .from('onboarding_account_configurations')
          .update({
            ...config,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id)
          .select()
          .single();

        if (error) throw error;
        return data as AccountConfiguration;
      } else {
        // Insert new
        const { data, error } = await (supabase as any)
          .from('onboarding_account_configurations')
          .insert({
            ...config,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (error) throw error;
        return data as AccountConfiguration;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['account-configurations', variables.project_id] });
      toast({
        title: 'Configuration Saved',
        description: 'Account configuration has been saved successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save configuration',
        variant: 'destructive',
      });
    },
  });
}

// Bulk save configurations for all accounts under a property
export function useBulkSaveConfigurations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      projectId,
      propertyName,
      config,
      accountIds,
    }: {
      projectId: string;
      propertyName: string;
      config: Partial<AccountConfiguration>;
      accountIds: string[];
    }) => {
      const results = [];

      for (const accountId of accountIds) {
        const { data: existing } = await (supabase as any)
          .from('onboarding_account_configurations')
          .select('id')
          .eq('project_id', projectId)
          .eq('imported_account_id', accountId)
          .maybeSingle();

        if (existing) {
          const { data, error } = await (supabase as any)
            .from('onboarding_account_configurations')
            .update({
              ...config,
              updated_at: new Date().toISOString(),
            })
            .eq('id', existing.id)
            .select()
            .single();

          if (error) throw error;
          results.push(data);
        } else {
          const { data, error } = await (supabase as any)
            .from('onboarding_account_configurations')
            .insert({
              project_id: projectId,
              imported_account_id: accountId,
              ...config,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .select()
            .single();

          if (error) throw error;
          results.push(data);
        }
      }

      return results;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['account-configurations', variables.projectId] });
      toast({
        title: 'Bulk Configuration Saved',
        description: `Configuration applied to ${variables.accountIds.length} accounts under ${variables.propertyName}.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save bulk configuration',
        variant: 'destructive',
      });
    },
  });
}

// Fetch parsing fields for workspace
export function useParsingFields(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['parsing-fields', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return getDefaultParsingFields();

      const { data, error } = await (supabase as any)
        .from('onboarding_parsing_fields')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      // If no custom fields, return defaults
      if (!data || data.length === 0) {
        return getDefaultParsingFields();
      }

      return data as ParsingField[];
    },
    enabled: true,
  });
}

// Create custom parsing field
export function useCreateParsingField() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (field: Omit<ParsingField, 'id' | 'created_at'>) => {
      const { data, error } = await (supabase as any)
        .from('onboarding_parsing_fields')
        .insert({
          ...field,
          created_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      return data as ParsingField;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['parsing-fields', variables.workspace_id] });
      toast({
        title: 'Field Created',
        description: 'Custom parsing field has been created.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create field',
        variant: 'destructive',
      });
    },
  });
}

// Default parsing fields
function getDefaultParsingFields(): ParsingField[] {
  const now = new Date().toISOString();
  return [
    { id: 'account_number', workspace_id: '', field_name: 'Account Number', field_type: 'text', is_default: true, is_mandatory: true, sort_order: 1, created_at: now },
    { id: 'bill_date', workspace_id: '', field_name: 'Bill Date', field_type: 'date', is_default: true, is_mandatory: true, sort_order: 2, created_at: now },
    { id: 'due_date', workspace_id: '', field_name: 'Due Date', field_type: 'date', is_default: true, is_mandatory: true, sort_order: 3, created_at: now },
    { id: 'amount_due', workspace_id: '', field_name: 'Amount Due', field_type: 'currency', is_default: true, is_mandatory: true, sort_order: 4, created_at: now },
    { id: 'billing_period_start', workspace_id: '', field_name: 'Billing Period Start', field_type: 'date', is_default: true, is_mandatory: false, sort_order: 5, created_at: now },
    { id: 'billing_period_end', workspace_id: '', field_name: 'Billing Period End', field_type: 'date', is_default: true, is_mandatory: false, sort_order: 6, created_at: now },
    { id: 'usage_amount', workspace_id: '', field_name: 'Usage Amount', field_type: 'number', is_default: true, is_mandatory: false, sort_order: 7, created_at: now },
    { id: 'usage_unit', workspace_id: '', field_name: 'Usage Unit', field_type: 'text', is_default: true, is_mandatory: false, sort_order: 8, created_at: now },
    { id: 'meter_number', workspace_id: '', field_name: 'Meter Number', field_type: 'text', is_default: true, is_mandatory: false, sort_order: 9, created_at: now },
    { id: 'previous_balance', workspace_id: '', field_name: 'Previous Balance', field_type: 'currency', is_default: true, is_mandatory: false, sort_order: 10, created_at: now },
    { id: 'current_charges', workspace_id: '', field_name: 'Current Charges', field_type: 'currency', is_default: true, is_mandatory: false, sort_order: 11, created_at: now },
    { id: 'late_fee', workspace_id: '', field_name: 'Late Fee', field_type: 'currency', is_default: true, is_mandatory: false, sort_order: 12, created_at: now },
  ];
}
